/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { NextDueCycleType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface NextDueProps {
  id: string;
  typeId: string;
  cycleType: NextDueCycleType;
  value: string;
  triggerValue: number;
  notes: string | null;
  inTolerance: boolean | null;
}

export class NextDueRecord extends Record<NextDueProps>({
  id: null as any,
  typeId: null as any,
  cycleType: null as any,
  value: null as any,
  triggerValue: null as any,
  notes: null as any,
  inTolerance: null as any,
}) {
  public static fromJS(json: {
    id: string;
    typeId: string;
    cycleType: NextDueCycleType;
    value: string;
    triggerValue: number;
    notes: string | null;
    inTolerance: boolean | null;
  }): NextDueRecord {
    if (json == null) {
      throw new Error('NextDueRecord.fromJS: parameter cannot be null');
    }
    return new NextDueRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'NextDueJson.id',
      ),
      typeId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'typeId', '00000000-0000-0000-0000-000000000000'),
        'NextDueJson.typeId',
      ),
      cycleType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'cycleType', NextDueCycleType.AF),
        'NextDueJson.cycleType',
      ),
      value: getFromOrThrow(json, (j) => j.value, 'NextDueJson.value'),
      triggerValue: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'triggerValue', 0),
        'NextDueJson.triggerValue',
      ),
      notes: json.notes,
      inTolerance: json.inTolerance,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    typeId: string;
    cycleType: NextDueCycleType;
    value: string;
    triggerValue: number;
    notes: string | null;
    inTolerance: boolean | null;
  }): [NextDueRecord, Error[]] {
    if (json == null) {
      throw new Error('NextDueRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new NextDueRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'NextDueJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      typeId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'typeId', '00000000-0000-0000-0000-000000000000'),
            'NextDueJson.typeId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cycleType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'cycleType', NextDueCycleType.AF),
            'NextDueJson.cycleType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      value: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.value, 'NextDueJson.value'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      triggerValue: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'triggerValue', 0),
            'NextDueJson.triggerValue',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notes: tryOrDefault(
        () => json.notes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      inTolerance: tryOrDefault(
        () => json.inTolerance,
        (err) => errors.push(err),
      )!,
    });

    return [record, errors];
  }

  constructor(props: NextDueProps) {
    super(props);
    if (props == null) {
      throw new Error('NextDueRecord constructor parameter cannot be null');
    }
  }
}

export abstract class NextDueViewModelBase implements NextDueProps {
  protected readonly _props: NextDueProps;
  private _json?: NextDueProps;

  get id(): string {
    return this._props.id;
  }
  get typeId(): string {
    return this._props.typeId;
  }
  get cycleType(): NextDueCycleType {
    return this._props.cycleType;
  }
  get value(): string {
    return this._props.value;
  }
  get triggerValue(): number {
    return this._props.triggerValue;
  }
  get notes(): string | null {
    return this._props.notes;
  }
  get inTolerance(): boolean | null {
    return this._props.inTolerance;
  }

  public get isAF() {
    return this._props.cycleType === NextDueCycleType.AF;
  }
  public get isE1() {
    return this._props.cycleType === NextDueCycleType.E1;
  }
  public get isE2() {
    return this._props.cycleType === NextDueCycleType.E2;
  }

  public isAnyCycleType(...nextDueCycleType: NextDueCycleType[]) {
    return nextDueCycleType.includes(this._props.cycleType);
  }

  constructor(props: NextDueProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        typeId: this._props.typeId,
        cycleType: this._props.cycleType,
        value: this._props.value,
        triggerValue: this._props.triggerValue,
        notes: this._props.notes,
        inTolerance: this._props.inTolerance,
      };
    }
    return this._json;
  }
}
